/**
 * Coverage AutoFill — CAR-1 Geotechnical fill.
 *
 * Pack: docs/prompt-packs/Coverage_AutoFill_Realworld_Research_Prompt.md (§CAR-1).
 *
 * Pure transform from NRCS SSURGO `GeotechnicalData` onto a bounded
 * screening subset of the `geotech` checklist requirement's authored field
 * keys. The professional geotechnical schema is intentionally broader;
 * downstream source policy prevents these public-screen candidates from
 * substituting for document-only professional facts.
 *
 * Structured field map (`authoredSchema` keys → SSURGO origin):
 *
 *   bearingCapacity_kPa    ← derived from USCS via
 *                              `inferBearingCapacityKPa`. NEVER set
 *                              when the USCS class is unknown (HARD
 *                              RULE 8 — no fabrication).
 *   soilClassification     ← `uscsClass` directly.
 *   boreholeDepth_m        ← always omitted — SSURGO publishes a soil
 *                              profile depth but NOT a borehole log.
 *                              Filling this would mislead the report
 *                              writer that real subsurface investigation
 *                              has happened when it has not. The notes
 *                              string flags the gap.
 *   groundwaterDepth_m     ← `depthToWaterTableCm / 100` (cm → m).
 *
 * Hallucination guard:
 *   - Every non-null structured field cites the SSURGO `mukey` via the
 *     `notes` string and the `sourceUrl`.
 *   - When `uscsClass` is null, NO bearing capacity is set (the input
 *     to `inferBearingCapacityKPa` would be undefined).
 *   - When `depthToWaterTableCm` is null, no `groundwaterDepth_m` is
 *     set — the dialog still renders the empty input.
 *
 * Confidence:
 *   - Routed through `inferAutoFillConfidence({ fillSource: 'provider',
 *     fieldsFilledCount, fieldsTotalCount })`. Field count comes from
 *     how many of the four SSURGO-compatible screening keys were filled.
 */
import {
  inferBearingCapacityKPa,
  type BearingCapacityConfidence,
} from '../geotechnical/bearingCapacity';
import type { GeotechnicalData } from '../../services/dataProviders/nrcsSoilSurvey';
import { inferAutoFillConfidence, type InferAutoFillConfidenceResult } from './confidenceTier';

/**
 * SSURGO-compatible `geotech` screening field keys. Every key remains in
 * the authored schema for compatibility, but this is deliberately not the
 * full professional schema.
 *
 * Pinned here so a checklist-side rename cannot silently drift the mapper,
 * and so professional-only conclusions are never added to public auto-fill.
 */
export const GEOTECH_FIELD_KEYS = [
  'bearingCapacity_kPa',
  'soilClassification',
  'boreholeDepth_m',
  'groundwaterDepth_m',
] as const;

/**
 * Structured field values keyed exactly the way the
 * AuthorEvidenceDialog expects on its
 * `AuthoredEvidenceFormState.fields[k]` shape. `notes` is sibling.
 */
export interface GeotechCoverageFill {
  fields: Partial<Record<typeof GEOTECH_FIELD_KEYS[number], string | number>>;
  /** Free-text notes — always populated with the autofill rationale +
   *  the "Commission a real geotechnical investigation" gate. */
  notes: string;
  /** How many of the four SSURGO-compatible screening fields were filled. */
  fieldsFilledCount: number;
  /** Always 4 — not the larger professional geotechnical schema count. */
  fieldsTotalCount: number;
  /** Public NRCS Web Soil Survey URL stamped on every fill. */
  sourceUrl: string;
  /** NRCS provenance tag. */
  sourceProvider: 'nrcs-soil-survey';
  /** Confidence band derived from `inferAutoFillConfidence` against
   *  `'provider'` source. */
  confidence: InferAutoFillConfidenceResult;
  /** The bearing-capacity helper's per-result confidence (0.45 or
   *  0.60). Surfaced separately so the dialog can show the engineering
   *  confidence next to the provider-fill confidence. */
  bearingCapacityConfidence: BearingCapacityConfidence | null;
}

const NRCS_WSS_URL = 'https://websoilsurvey.sc.egov.usda.gov/App/HomePage.htm';

/**
 * Map a `GeotechnicalData` payload to the geotech coverage shape.
 *
 * @param geo  NRCS SSURGO provider response.
 * @param providerFreshnessDays  CAR-8 follow-up — when ≥ 90 days the
 *   confidence band is downgraded one tier (high→medium, medium→low).
 *   SSURGO publishes a static national soil dataset (no per-fetch
 *   timestamp on the chain's `GeotechnicalData` shape), so callers
 *   that don't track freshness should pass `undefined` and the
 *   helper preserves current behaviour.
 */
export function mapGeotechnicalDataToCoverage(
  geo: GeotechnicalData,
  providerFreshnessDays?: number,
): GeotechCoverageFill {
  const fields: GeotechCoverageFill['fields'] = {};
  let filled = 0;
  let bearingCapacityConfidence: BearingCapacityConfidence | null = null;
  let bearingRationale: string | null = null;

  // ── soilClassification ──
  // Only fill when we have a USCS class — the dialog renders this as a
  // <select> bound to the schema's enum. Setting a value outside the
  // enum would break the select.
  if (geo.uscsClass) {
    fields.soilClassification = geo.uscsClass;
    filled += 1;
  }

  // ── groundwaterDepth_m ──
  // SSURGO records depth-to-water-table in cm; the dialog expects m.
  // The /100 conversion is a unit-bridge at the boundary — engineering
  // rule §4 (unit consistency). Never invent a depth.
  const groundwaterDepthM =
    geo.depthToWaterTableCm != null ? geo.depthToWaterTableCm / 100 : null;
  if (groundwaterDepthM != null) {
    // Round to one decimal to match the dialog's typical numeric
    // input granularity; raw SSURGO values are integer cm.
    fields.groundwaterDepth_m = parseFloat(groundwaterDepthM.toFixed(1));
    filled += 1;
  }

  // ── bearingCapacity_kPa ──
  // Inferred only when USCS class is known. The bearing capacity helper
  // applies the conservative Terzaghi water-table reduction when
  // groundwater is in the failure zone.
  if (geo.uscsClass) {
    const bc = inferBearingCapacityKPa({
      uscsClass: geo.uscsClass,
      groundwaterDepthM,
    });
    fields.bearingCapacity_kPa = bc.value;
    filled += 1;
    bearingCapacityConfidence = bc.confidence;
    bearingRationale = bc.rationale;
  }

  // ── boreholeDepth_m ──
  // INTENTIONALLY OMITTED. SSURGO has no boring-log data; filling this
  // would suggest a real subsurface investigation has happened. The
  // gap is flagged in `notes` so the user understands why the input
  // is empty.

  const notes = buildGeotechNotes({
    geo,
    bearingRationale,
    groundwaterDepthM,
  });

  const confidence = inferAutoFillConfidence({
    fillSource: 'provider',
    fieldsFilledCount: filled,
    fieldsTotalCount: GEOTECH_FIELD_KEYS.length,
    // CAR-8 follow-up: only forwarded when the caller supplied a
    // finite freshness signal. `Number.isFinite()` guard keeps NaN /
    // ±Infinity from leaking into the cap check.
    providerFreshnessDays:
      Number.isFinite(providerFreshnessDays) ? providerFreshnessDays : undefined,
  });

  return {
    fields,
    notes,
    fieldsFilledCount: filled,
    fieldsTotalCount: GEOTECH_FIELD_KEYS.length,
    sourceUrl: NRCS_WSS_URL,
    sourceProvider: 'nrcs-soil-survey',
    confidence,
    bearingCapacityConfidence,
  };
}

/**
 * Build the `notes` string. Always frames the fill as auto-filled and
 * always includes the "real geotechnical investigation required" gate.
 */
function buildGeotechNotes(args: {
  geo: GeotechnicalData;
  bearingRationale: string | null;
  groundwaterDepthM: number | null;
}): string {
  const parts: string[] = [];
  parts.push('Auto-filled from NRCS Web Soil Survey (SSURGO).');

  // Cite the exact map unit so the auditor can trace the value.
  const mu = args.geo.mapUnitName
    ? `${args.geo.mukey}: "${args.geo.mapUnitName}"`
    : args.geo.mukey;
  parts.push(`SSURGO map unit ${mu}.`);

  if (args.geo.uscsClass && args.geo.usdaTexture) {
    parts.push(
      `USCS class ${args.geo.uscsClass} derived from USDA texture ` +
        `"${args.geo.usdaTexture}" per NRCS NSSH Table 423-A.5.`,
    );
  } else if (!args.geo.uscsClass) {
    parts.push(
      'USDA texture not classifiable to a USCS class with the NSSH 423-A.5 ' +
        'mapping — Soil classification (USCS) field left blank. A real ' +
        'sieve / Atterberg test result is required.',
    );
  }

  if (args.geo.drainageClass) {
    parts.push(`Drainage class: ${args.geo.drainageClass}.`);
  }

  if (args.geo.depthToBedrockCm != null) {
    parts.push(`Depth to bedrock (SSURGO min): ${(args.geo.depthToBedrockCm / 100).toFixed(1)} m.`);
  }

  if (args.groundwaterDepthM != null) {
    parts.push(
      `Groundwater depth from SSURGO seasonal water-table minimum: ` +
        `${args.groundwaterDepthM.toFixed(1)} m.`,
    );
  } else {
    parts.push(
      'Groundwater depth not in SSURGO for this map unit — left blank. ' +
        'A USGS NWIS lookup for the nearest active well can fill this gap ' +
        '(CAR-1 follow-up).',
    );
  }

  if (args.bearingRationale) {
    parts.push(`Bearing capacity rationale: ${args.bearingRationale}`);
  }

  parts.push(
    'Borehole depth left blank — SSURGO does not publish boring-log ' +
      'data. A real Phase II geotechnical investigation is required ' +
      'before foundation design.',
  );
  parts.push(
    'Auto-fill is a feasibility-stage estimate only — values are ' +
      'CONSERVATIVE and intended for early-stage sizing. ' +
      'Commission a site-specific geotechnical investigation before ' +
      'finalising the foundation design.',
  );

  return parts.join(' ');
}
